import PropTypes from 'prop-types';
import classNames from 'classnames';
import { trans, useSoyuzExperiment } from '@spotahome/soyuz/client';

import { useMediatorModalHandle } from '../utils/withModalHandle';
import { OPEN_INSTANT_BOOKING_MODAL } from '../utils/constants';

import Modal from '../Modal';
import ModalBody from '../ModalBody';
import ModalHeader from '../ModalHeader';
import ModalFooter from '../ModalFooter';
import Button from '../Button';

import { DSIcon } from '../../../base';

import AgeRange from './rules/AgeRange';
import BookingEarly from './rules/BookingEarly';
import CouplesNotAllowed from './rules/CouplesNotAllowed';
import MinStay from './rules/MinStay';
import Gender from './rules/Gender';

import styles from './InstantBookingModal.module.scss';

const RULES = [
  'ageRange',
  'gender',
  'couplesNotAllowed',
  'bookingTooEarly',
  'minStay'
];

const rulesCorrelation = {
  ageRange: props => <AgeRange {...props} />,
  bookingTooEarly: props => <BookingEarly {...props} />,
  minStay: props => <MinStay {...props} />,
  couplesNotAllowed: () => <CouplesNotAllowed />,
  gender: props => <Gender {...props} />
};

const VariantLabel = () => (
  <span className={styles['instant-modal__header-label-with-icon']}>
    <DSIcon name="electricity_bill" size="l" />
    {trans('property_show.book_now_card.instant_booking')}
  </span>
);

const Rules = ({ rules }) => (
  <ul className={styles['instant-modal__rules-list']}>
    {RULES.map(ruleType => {
      const rule = rules.find(r => r.type === ruleType) || {};
      const RuleComponent = rulesCorrelation[rule.type];

      return RuleComponent ? (
        <RuleComponent key={ruleType} {...rule.params} />
      ) : null;
    })}
  </ul>
);

const OriginalBody = ({ rules }) => (
  <>
    <p
      className={styles['instant-modal__text-title instant-modal__text-block']}
    >
      {trans('property_show.instant_booking_modal.title')}
    </p>
    <p className={styles['instant-modal__text-block']}>
      {trans('property_show.instant_booking_modal.description.1')}
    </p>
    <p className={styles['instant-modal__text-block']}>
      {trans('property_show.instant_booking_modal.description.2')}
    </p>
    <Rules rules={rules} />
    <p
      className={styles['instant-modal__text-block']}
      dangerouslySetInnerHTML={{
        __html: trans('property_show.instant_booking_modal.check_rules')
      }}
    />
    <p className={styles['instant-modal__text-block']}>
      {trans('property_show.instant_booking_modal.dont_worry')}
    </p>
  </>
);

const VariantBody = ({ rules }) => (
  <>
    <p
      className={styles['instant-modal__text-title instant-modal__text-block']}
    >
      {trans('property_show.instant_booking_modal.description.variant')}
    </p>
    <div>
      <h5 className={styles['instant-modal__title-block']}>
        {trans('property_show.instant_booking_modal.criteria.title')}
      </h5>
      <Rules rules={rules} />
    </div>
    <div>
      <h5 className={styles['instant-modal__title-block']}>
        {trans('property_show.instant_booking_modal.what-if.title')}
      </h5>
      <p className={styles['instant-modal__text-block']}>
        {trans('property_show.instant_booking_modal.what-if.description')}
      </p>
    </div>
  </>
);

const InstantBookingModal = ({ rules }) => {
  const { isModalOpen, handleModalClose } = useMediatorModalHandle({
    channel: OPEN_INSTANT_BOOKING_MODAL
  });

  const isListingSimplificationHeaderVariant =
    useSoyuzExperiment('ListingSimplificationHeader').get('behaviour') ===
    'variant';

  const modalClasses = classNames(styles['instant-modal'], {
    [styles['instant-modal--simplification-header-variant']]:
      isListingSimplificationHeaderVariant
  });

  const headerClasses = classNames(styles['instant-modal__header'], {
    [styles['instant-modal__header--simplification-header-variant']]:
      isListingSimplificationHeaderVariant
  });

  const bodyClasses = classNames(styles['instant-modal__block'], {
    [styles['instant-modal__block--simplification-header-variant']]:
      isListingSimplificationHeaderVariant
  });

  return isModalOpen ? (
    <Modal
      isOpen={isModalOpen}
      contentLabel="modal"
      onRequestClose={handleModalClose}
      size={isListingSimplificationHeaderVariant ? 'medium' : 'big'}
      className={modalClasses}
    >
      <ModalHeader
        className={headerClasses}
        onRequestClose={handleModalClose}
        label={
          isListingSimplificationHeaderVariant ? (
            <VariantLabel />
          ) : (
            trans('property_show.instant_booking_modal.header')
          )
        }
      />
      <ModalBody className={bodyClasses}>
        {isListingSimplificationHeaderVariant ? (
          <VariantBody rules={rules} />
        ) : (
          <OriginalBody rules={rules} />
        )}
      </ModalBody>
      {!isListingSimplificationHeaderVariant && (
        <ModalFooter className={styles['instant-modal__block']}>
          <Button onClick={handleModalClose} size="big" color="interaction">
            {trans('property_show.instant_booking_modal.understood')}
          </Button>
        </ModalFooter>
      )}
    </Modal>
  ) : null;
};

InstantBookingModal.propTypes = {
  rules: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        params: PropTypes.shape(),
        type: PropTypes.string
      })
    ),
    PropTypes.shape({
      params: PropTypes.shape(),
      type: PropTypes.string
    })
  ]).isRequired
};

export default InstantBookingModal;
